import React, { useState } from 'react';
import './Dashboard.css';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

function Dashboard({ 
  students, 
  studentRsvps,
  staff, 
  staffRsvps,
  studentRespondents,
  staffRespondents,
  studentNonRespondents,
  staffNonRespondents
}) {
  const [activeTab, setActiveTab] = useState('overview');
  
  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };
  
  // Calculate stats for the overview
  const totalStudents = students.length;
  const totalStaff = staff.length;
  const totalRsvpCount = studentRespondents.length + staffRespondents.length;
  const totalAttendees = studentRespondents.reduce((acc, rsvp) => acc + (rsvp.guestCount || 0) + 1, 0) +
                        staffRespondents.reduce((acc, rsvp) => acc + (rsvp.guestCount || 0) + 1, 0);
  
  // Calculate response rates
  const studentResponseRate = totalStudents > 0 ? (studentRespondents.length / totalStudents) * 100 : 0;
  const staffResponseRate = totalStaff > 0 ? (staffRespondents.length / totalStaff) * 100 : 0;
  const overallResponseRate = (totalStudents + totalStaff) > 0 ? 
    ((studentRespondents.length + staffRespondents.length) / (totalStudents + totalStaff)) * 100 : 0;
  
  // Get guest counts for distribution chart
  const guestCounts = {};
  [...studentRespondents, ...staffRespondents].forEach(rsvp => {
    const count = rsvp.guestCount || 0;
    guestCounts[count] = (guestCounts[count] || 0) + 1;
  });
  
  // Prepare pie chart data for response rates
  const responseRateData = {
    labels: ['Students Responded', 'Students Not Responded', 'Staff Responded', 'Staff Not Responded'],
    datasets: [
      {
        data: [
          studentRespondents.length,
          studentNonRespondents.length,
          staffRespondents.length,
          staffNonRespondents.length
        ],
        backgroundColor: [
          'rgba(244, 162, 97, 0.85)', // horizon-gold (respondents)
          'rgba(244, 162, 97, 0.30)',
          'rgba(59, 90, 140, 0.80)',  // horizon-blue (staff respondents)
          'rgba(59, 90, 140, 0.30)'
        ],
        borderColor: [
          'rgba(244, 162, 97, 1)',
          'rgba(244, 162, 97, 0.55)',
          'rgba(59, 90, 140, 1)',
          'rgba(59, 90, 140, 0.55)'
        ],
        borderWidth: 1,
      },
    ],
  };
  
  // Prepare bar chart data for guest distribution
  const guestDistributionData = {
    labels: Object.keys(guestCounts).sort((a, b) => Number(a) - Number(b)),
    datasets: [
      {
        label: 'Number of RSVPs',
        data: Object.keys(guestCounts).sort((a, b) => Number(a) - Number(b)).map(key => guestCounts[key]),
        backgroundColor: 'rgba(201, 122, 42, 0.6)', // horizon-amber
        borderColor: 'rgba(201, 122, 42, 1)',
        borderWidth: 1,
      },
    ],
  };
  
  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Guest Distribution',
        color: '#1a1a1a',
        font: {
          size: 16,
          weight: 'bold'
        }
      },
    },
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          color: '#1a1a1a',
          padding: 15,
          font: {
            size: 12
          }
        }
      },
      title: {
        display: true,
        text: 'Response Status',
        color: '#1a1a1a',
        font: {
          size: 16,
          weight: 'bold'
        }
      },
    },
  };
  
  // Most recent RSVPs (10 most recent)
  const allRsvps = [...studentRsvps, ...staffRsvps]
    .filter(rsvp => rsvp.updatedAt)
    .sort((a, b) => {
      const dateA = a.updatedAt?.toDate ? a.updatedAt.toDate() : new Date(a.updatedAt);
      const dateB = b.updatedAt?.toDate ? b.updatedAt.toDate() : new Date(b.updatedAt);
      return dateB - dateA;
    })
    .slice(0, 10);
  
  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>RSVP Dashboard</h1>
        <div className="event-details">
          <h3>Event Information</h3>
          <div className="event-detail-row">
            <span className="event-label">Date:</span>
            <span className="event-value">May 15, 2025</span>
          </div>
          <div className="event-detail-row">
            <span className="event-label">Time:</span>
            <span className="event-value">6:00 PM - 8:30 PM</span>
          </div>
          <div className="event-detail-row">
            <span className="event-label">Location:</span>
            <span className="event-value">Veterans Memorial High School Auditorium</span>
          </div>
        </div>
      </div>
      
      <div className="dashboard-tabs">
        <button 
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`} 
          onClick={() => handleTabChange('overview')}
        >
          Overview
        </button>
        <button 
          className={`tab-button ${activeTab === 'stats' ? 'active' : ''}`} 
          onClick={() => handleTabChange('stats')}
        >
          Statistics
        </button>
        <button 
          className={`tab-button ${activeTab === 'recent' ? 'active' : ''}`} 
          onClick={() => handleTabChange('recent')}
        >
          Recent Activity
        </button>
      </div>
      
      <div className="dashboard-content">
        {activeTab === 'overview' && (
          <div className="dashboard-overview">
            <div className="stat-cards">
              <div className="stat-card overall-card">
                <h3 className="stat-title">Overall Response Rate</h3>
                <div className="stat-number">{overallResponseRate.toFixed(1)}%</div>
                <p className="stat-description">{totalRsvpCount} of {totalStudents + totalStaff} total invitees</p>
                <div className="progress-container">
                  <div className="progress-bar" style={{ width: `${overallResponseRate}%` }}></div>
                </div>
              </div>
              
              <div className="stat-card student-card">
                <h3 className="stat-title">Student Response Rate</h3>
                <div className="stat-number">{studentResponseRate.toFixed(1)}%</div>
                <p className="stat-description">{studentRespondents.length} of {totalStudents}</p>
                <div className="progress-container">
                  <div className="progress-bar student-progress" style={{ width: `${studentResponseRate}%` }}></div>
                </div>
              </div>
              
              <div className="stat-card staff-card">
                <h3 className="stat-title">Staff Response Rate</h3>
                <div className="stat-number">{staffResponseRate.toFixed(1)}%</div>
                <p className="stat-description">{staffRespondents.length} of {totalStaff}</p>
                <div className="progress-container">
                  <div className="progress-bar staff-progress" style={{ width: `${staffResponseRate}%` }}></div>
                </div>
              </div>
              
              <div className="stat-card nonresponder-card">
                <h3 className="stat-title">Total Attendees</h3>
                <div className="stat-number">{totalAttendees}</div>
                <p className="stat-description">Including {totalRsvpCount} respondents and their guests</p>
              </div>
            </div>
            
            <div className="chart-container">
              <div className="chart-card">
                <div className="chart-wrapper">
                  <Pie data={responseRateData} options={pieChartOptions} />
                </div>
              </div>
              
              <div className="chart-card">
                <div className="chart-wrapper">
                  <Bar data={guestDistributionData} options={chartOptions} />
                </div>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'stats' && (
          <div className="dashboard-stats">
            <div className="stats-grid">
              <div className="stat-section">
                <h3>Response Stats</h3>
                <table className="stats-table">
                  <tbody>
                    <tr>
                      <td>Overall Response Rate:</td>
                      <td>{overallResponseRate.toFixed(1)}%</td>
                    </tr>
                    <tr>
                      <td>Student Response Rate:</td>
                      <td>{studentResponseRate.toFixed(1)}%</td>
                    </tr>
                    <tr>
                      <td>Staff Response Rate:</td>
                      <td>{staffResponseRate.toFixed(1)}%</td>
                    </tr>
                    <tr>
                      <td>Total Students:</td>
                      <td>{totalStudents}</td>
                    </tr>
                    <tr>
                      <td>Student Respondents:</td>
                      <td>{studentRespondents.length}</td>
                    </tr>
                    <tr>
                      <td>Student Non-Respondents:</td>
                      <td>{studentNonRespondents.length}</td>
                    </tr>
                    <tr>
                      <td>Total Staff:</td>
                      <td>{totalStaff}</td>
                    </tr>
                    <tr>
                      <td>Staff Respondents:</td>
                      <td>{staffRespondents.length}</td>
                    </tr>
                    <tr>
                      <td>Staff Non-Respondents:</td>
                      <td>{staffNonRespondents.length}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div className="stat-section">
                <h3>Attendance Stats</h3>
                <table className="stats-table">
                  <tbody>
                    <tr>
                      <td>Total Expected Attendees:</td>
                      <td>{totalAttendees}</td>
                    </tr>
                    <tr>
                      <td>Students Attending:</td>
                      <td>{studentRespondents.length}</td>
                    </tr>
                    <tr>
                      <td>Staff Attending:</td>
                      <td>{staffRespondents.length}</td>
                    </tr>
                    <tr>
                      <td>Student Guests:</td>
                      <td>{studentRespondents.reduce((acc, rsvp) => acc + (rsvp.guestCount || 0), 0)}</td>
                    </tr>
                    <tr>
                      <td>Staff Guests:</td>
                      <td>{staffRespondents.reduce((acc, rsvp) => acc + (rsvp.guestCount || 0), 0)}</td>
                    </tr>
                    <tr>
                      <td>Average Guests Per Student:</td>
                      <td>
                        {studentRespondents.length > 0 
                          ? (studentRespondents.reduce((acc, rsvp) => acc + (rsvp.guestCount || 0), 0) / studentRespondents.length).toFixed(1) 
                          : '0.0'}
                      </td>
                    </tr>
                    <tr>
                      <td>Average Guests Per Staff:</td>
                      <td>
                        {staffRespondents.length > 0 
                          ? (staffRespondents.reduce((acc, rsvp) => acc + (rsvp.guestCount || 0), 0) / staffRespondents.length).toFixed(1) 
                          : '0.0'}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'recent' && (
          <div className="dashboard-recent">
            <h3>Recent RSVP Activity</h3>
            
            {allRsvps.length > 0 ? (
              <div className="responsive-table-container">
                <table className="recent-activity-table">
                  <thead>
                    <tr>
                      <th>Date/Time</th>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Guests</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allRsvps.map((rsvp, index) => {
                      // Determine if this is a student or staff RSVP
                      const isStudent = rsvp.studentId || students.some(s => s.id === rsvp.id);
                      
                      // Find the corresponding student or staff record
                      const person = isStudent
                        ? students.find(s => s.id === (rsvp.studentId || rsvp.id))
                        : staff.find(s => s.username === (rsvp.staffUsername || rsvp.username));
                      
                      // Format the date
                      const date = rsvp.updatedAt?.toDate 
                        ? rsvp.updatedAt.toDate() 
                        : new Date(rsvp.updatedAt);
                      
                      return (
                        <tr key={index}>
                          <td>{date.toLocaleString()}</td>
                          <td>{person?.name || 'Unknown'}</td>
                          <td>
                            <span className={`type-badge ${isStudent ? 'student' : 'staff'}`}>
                              {isStudent ? 'Student' : 'Staff'}
                            </span>
                          </td>
                          <td>{rsvp.guestCount || 0}</td>
                          <td>
                            <span className={`status-badge ${rsvp.status === 'confirmed' ? 'confirmed' : 'not-confirmed'}`}>
                              {rsvp.status === 'confirmed' ? 'Confirmed' : 'In Progress'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="no-data-message">No recent RSVP activity</p>
            )}
          </div>
        )}
      </div>
      
      <div className="quick-actions">
        <h2>Quick Actions</h2>
        <div className="actions-grid">
          <button className="action-button">
            <span className="action-icon">✉️</span>
            Send Reminders
          </button>
          <button className="action-button export">
            <span className="action-icon">📊</span>
            Export Reports
          </button>
          <button className="action-button reminder">
            <span className="action-icon">📝</span>
            Print Name Tags
          </button>
          <button className="action-button">
            <span className="action-icon">🔄</span>
            Sync Data
          </button>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;